// apps/admin/src/features/products/SelectProductsDialog.tsx
//
// dbg-20260803-025413 — "Select Products" picker for the Category form
// (Figma "Fulfillment V3" frame `select-products-dialog`).
//
// The design says "Products", but there is no Product model in this codebase:
// the catalog's product definitions are Blueprints, linked to a category both
// ways (Blueprint.categoryId and Category.blueprintIds[]). So this dialog
// picks Blueprints and the design's "Blueprint" column renders the blueprint
// code; "SKU Count" is the blueprint's variant count.
//
// Selection is local until Save — the caller receives the new id list and
// decides when to persist (the Category form persists on its own Save, so a
// cancelled category edit does not leave product links behind).
//
// Selection survives paging and filtering because it is held as an id Set,
// not as a per-row checkbox state.
//
// A blueprint already linked by ANOTHER category renders disabled, with that
// category named on the row (dbg-20260811-180000). Before this, the picker let
// you tick it and the server refused the whole form on Create with a duplicate
// error naming ids — so finding which of twenty picks was the offender meant
// unticking by trial. `linkedCategory` comes off the list row and is derived
// server-side from `Category.blueprintIds`, the same field the save-time guard
// reads; `Blueprint.categoryId` is a legacy FK that is allowed to drift from it
// and would disable the wrong rows.
//
// A row that is BOTH taken and already in `value` stays unticked-able: that is
// pre-existing drift, and locking it would leave the form unsavable with no way
// to correct it from here.
//
// "Show selected only" fetches the selected blueprints BY ID rather than
// filtering the current page (dbg-20260803-100937). The old version kept a
// cache of rows the user had scrolled past, so a category whose 236 links were
// spread across pages rendered only the handful from the page that happened to
// be loaded — the rest looked unselected. Ids are chunked because the list
// endpoint caps pageSize at 200 and a category may link up to 500 blueprints.

import { useEffect, useMemo, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  Badge,
  Button,
  Dialog,
  DialogTitle,
  NativeSelect,
  Pagination,
  SearchInput,
  Spinner,
  Switch,
  cn,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import { listAdminBlueprints } from '../../api/catalog.api.js';
import type { BlueprintStatus } from '../../types/catalog.js';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;

/**
 * The blueprint list row is a `passthrough()` zod object — the columns this
 * dialog needs are present at runtime (see blueprint.admin.service.ts
 * projection) but not in the strict type. Narrow them here rather than
 * loosening the shared schema.
 */
interface BlueprintPickerRow {
  id: string;
  name: string;
  blueprintCode: string;
  status: string;
  variantCount: number;
  thumbnailUrl: string | null;
  /** Category that already links this blueprint, or null when free. */
  linkedCategory: { id: string; name: string } | null;
}

function toPickerRow(raw: Record<string, unknown>): BlueprintPickerRow {
  const name = raw['name'];
  return {
    id: String(raw['id'] ?? raw['_id'] ?? ''),
    name:
      typeof name === 'string'
        ? name
        : typeof name === 'object' && name !== null
          ? String((name as Record<string, unknown>)['en'] ?? '')
          : '',
    blueprintCode: String(raw['blueprintCode'] ?? ''),
    status: String(raw['status'] ?? ''),
    variantCount:
      typeof raw['variantCount'] === 'number' ? (raw['variantCount'] as number) : 0,
    thumbnailUrl:
      typeof raw['thumbnailUrl'] === 'string' ? (raw['thumbnailUrl'] as string) : null,
    linkedCategory: (() => {
      const linked = raw['linkedCategory'];
      if (typeof linked !== 'object' || linked === null) return null;
      const l = linked as Record<string, unknown>;
      const id = typeof l['id'] === 'string' ? l['id'] : '';
      if (id === '') return null;
      return { id, name: typeof l['name'] === 'string' ? l['name'] : '' };
    })(),
  };
}

const ID_CHUNK = 200;

export interface SelectProductsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Currently linked blueprint ids. */
  value: string[];
  /** Fired on Save with the new id list. */
  onSave: (next: string[]) => void;
  /**
   * The category being edited, when there is one. Rows linked by a DIFFERENT
   * category are disabled; this row's own links must stay selectable, so edit
   * mode has to say which category it is. Omitted on create — nothing is
   * "its own" yet.
   */
  ownerCategoryId?: string;
  /**
   * Whether one product may belong to only one owner of this kind
   * (dbg-20260813).
   *
   * `true` (the default) is the CATEGORY rule: a product lives in exactly one
   * category, so a row another category already links renders disabled and
   * names its owner — the picker agreeing with `assertProductsNotTaken` in
   * category.admin.service.ts, which would otherwise reject the whole form.
   *
   * `false` is the COLLECTION rule: a collection is a grouping, and the same
   * product may appear in any number of them. The two taxonomies are separate
   * lists on the same screen, so a product's category must not gate its
   * collections — that was the bug: this dialog derives "taken" from
   * `linkedCategory`, and the Collection tab rendered it with the category
   * rule, disabling every product that had a category at all.
   */
  exclusive?: boolean;
  /**
   * Open with "Show selected only" already on. The list page's per-row
   * products action uses this: the admin asked to see what a row contains, so
   * starting on the full catalogue would bury the answer.
   */
  defaultShowSelectedOnly?: boolean;
  /** Disables the checkboxes and the Save button. */
  readOnly?: boolean;
}

export function SelectProductsDialog({
  open,
  onOpenChange,
  value,
  onSave,
  ownerCategoryId,
  exclusive = true,
  defaultShowSelectedOnly = false,
  readOnly = false,
}: SelectProductsDialogProps): JSX.Element {
  const { t } = useTranslation('catalog');

  const [selected, setSelected] = useState<Set<string>>(() => new Set(value));
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | BlueprintStatus>('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(defaultShowSelectedOnly);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Re-seed from props whenever the dialog is (re)opened — a cancelled edit
  // must not leak its staged selection into the next open.
  useEffect(() => {
    if (open) {
      setSelected(new Set(value));
      setSearch('');
      setStatusFilter('');
      setShowSelectedOnly(defaultShowSelectedOnly);
      setPage(1);
    }
    // `value` is intentionally excluded: re-seeding on every parent render
    // would discard in-dialog edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const listQuery = useQuery({
    queryKey: ['admin', 'blueprints', 'picker', { search, statusFilter, page, pageSize }],
    queryFn: () =>
      listAdminBlueprints({
        page,
        pageSize,
        ...(search.trim() !== '' ? { search: search.trim() } : {}),
        ...(statusFilter !== '' ? { status: statusFilter } : {}),
      }),
    // "Show selected only" renders `selectedQuery` instead, so paging the
    // catalogue here would just burn a request per keystroke of a search the
    // user cannot see the result of.
    enabled: open && !showSelectedOnly,
    staleTime: 30_000,
  });

  const rows = useMemo<BlueprintPickerRow[]>(
    () =>
      (listQuery.data?.items ?? []).map((r) =>
        toPickerRow(r as unknown as Record<string, unknown>),
      ),
    [listQuery.data],
  );

  // Fetch the selected blueprints by id, regardless of which page they live
  // on. Keyed on the sorted id list so toggling a checkbox does not refetch
  // when the set is unchanged in content.
  const selectedIds = useMemo(() => Array.from(selected).sort(), [selected]);
  const selectedQuery = useQuery({
    queryKey: ['admin', 'blueprints', 'picker', 'byIds', selectedIds],
    queryFn: async () => {
      const chunks: string[][] = [];
      for (let i = 0; i < selectedIds.length; i += ID_CHUNK) {
        chunks.push(selectedIds.slice(i, i + ID_CHUNK));
      }
      const pages = await Promise.all(
        chunks.map((ids) => listAdminBlueprints({ page: 1, pageSize: ID_CHUNK, ids })),
      );
      return pages.flatMap((r) => r.items);
    },
    enabled: open && showSelectedOnly && selectedIds.length > 0,
    staleTime: 30_000,
  });

  const selectedRows = useMemo<BlueprintPickerRow[]>(
    () =>
      (selectedQuery.data ?? []).map((r) =>
        toPickerRow(r as unknown as Record<string, unknown>),
      ),
    [selectedQuery.data],
  );

  // The toolbar filters have to be re-applied by hand in "show selected only"
  // mode: `selectedQuery` fetches BY ID so the server never sees `search` or
  // `statusFilter` (dbg-20260805-070735). Filtering locally is exact here —
  // every selected row is already in memory, unlike the paged catalogue.
  const visibleRows = useMemo<BlueprintPickerRow[]>(() => {
    if (!showSelectedOnly) return rows;
    const term = search.trim().toLowerCase();
    return selectedRows.filter((r) => {
      if (statusFilter !== '' && r.status !== statusFilter) return false;
      if (term === '') return true;
      return (
        r.name.toLowerCase().includes(term)
        || r.blueprintCode.toLowerCase().includes(term)
      );
    });
  }, [showSelectedOnly, rows, selectedRows, search, statusFilter]);

  const total = listQuery.data?.pagination.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Header checkbox follows what is on screen. Keying it off `rows` meant that
  // in selected-only mode it reported — and toggled — the catalogue page the
  // user was not looking at.
  // Only the rows that CAN be picked count, otherwise a page holding one taken
  // row could never show the header box as checked.
  const selectableRows = visibleRows.filter((r) => !takenByOther(r));
  const allOnPageSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.id));

  /**
   * Linked by somebody else. `ownerCategoryId` is undefined on create, so every
   * linked row counts as taken there — which is exactly right.
   *
   * Non-exclusive owners (collections) never treat a row as taken: the only
   * thing this dialog knows about is which CATEGORY holds a product, which says
   * nothing about whether a collection may also have it.
   */
  function takenByOther(row: BlueprintPickerRow): boolean {
    if (!exclusive) return false;
    return row.linkedCategory !== null && row.linkedCategory.id !== ownerCategoryId;
  }

  function toggleRow(id: string, next: boolean): void {
    setSelected((prev) => {
      const s = new Set(prev);
      if (next) s.add(id);
      else s.delete(id);
      return s;
    });
  }

  function toggleAllOnPage(next: boolean): void {
    setSelected((prev) => {
      const s = new Set(prev);
      for (const row of visibleRows) {
        // Select-all must not pick up the rows the server would reject; it can
        // still CLEAR anything, including a row that drifted in.
        if (next) {
          if (!takenByOther(row)) s.add(row.id);
        } else s.delete(row.id);
      }
      return s;
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      className="max-w-4xl"
      aria-label={t('category.products.dialogTitle')}
    >
      <div className="flex flex-col gap-4" data-testid="category-select-products-dialog">
        <div>
          {/* Radix warns unless the content declares a DialogTitle — a plain
              <h2> leaves screen readers with an unnamed dialog. */}
          <DialogTitle className="text-lg font-semibold text-pw-text">
            {t('category.products.dialogTitle')}
          </DialogTitle>
          <p className="mt-1 text-sm text-pw-text-muted">
            {t('category.products.dialogSubtitle')}
          </p>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full max-w-xs">
            <SearchInput
              value={search}
              onValueChange={(next) => {
                setSearch(next);
                setPage(1);
              }}
              placeholder={t('category.products.searchPlaceholder')}
              aria-label={t('category.products.searchPlaceholder')}
              data-testid="category-products-search"
            />
          </div>

          <NativeSelect
            className="w-auto"
            value={statusFilter}
            aria-label={t('category.products.filterStatusAria')}
            data-testid="category-products-filter-status"
            onChange={(e) => {
              setStatusFilter(e.target.value as '' | BlueprintStatus);
              setPage(1);
            }}
          >
            <option value="">{t('category.products.filterStatusAll')}</option>
            <option value="Active">{t('category.products.statusActive')}</option>
            <option value="Draft">{t('category.products.statusDraft')}</option>
            <option value="Inactive">{t('category.products.statusInactive')}</option>
          </NativeSelect>

          <label className="ml-auto flex items-center gap-2 text-sm text-pw-text">
            <Switch
              checked={showSelectedOnly}
              onCheckedChange={setShowSelectedOnly}
              aria-label={t('category.products.showSelectedOnly')}
              data-testid="category-products-selected-only"
            />
            {t('category.products.showSelectedOnly')}
          </label>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border border-pw-border">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="border-b border-pw-border bg-pw-surface/60">
                <th className="w-12 px-3 py-2.5 text-left">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    disabled={readOnly || visibleRows.length === 0}
                    onChange={(e) => toggleAllOnPage(e.target.checked)}
                    aria-label={t('category.products.selectAllOnPage')}
                    data-testid="category-products-select-all"
                    className="h-4 w-4 cursor-pointer accent-pw-primary"
                  />
                </th>
                <th className="w-20 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-pw-text-muted">
                  {t('category.products.colImage')}
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-pw-text-muted">
                  {t('category.products.colName')}
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-pw-text-muted">
                  {t('category.products.colBlueprint')}
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-pw-text-muted">
                  {t('category.products.colSkuCount')}
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-pw-text-muted">
                  {t('category.products.colStatus')}
                </th>
              </tr>
            </thead>
            <tbody>
              {(showSelectedOnly ? selectedQuery.isLoading : listQuery.isLoading) ? (
                <tr>
                  <td colSpan={6} className="px-3 py-10">
                    <div className="flex justify-center">
                      <Spinner />
                    </div>
                  </td>
                </tr>
              ) : (showSelectedOnly ? selectedQuery.isError : listQuery.isError) ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-10 text-center text-sm text-red-700 dark:text-red-300"
                  >
                    {t('category.products.loadError')}
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-10 text-center text-sm text-pw-text-muted"
                  >
                    {t('category.products.empty')}
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => {
                  const checked = selected.has(row.id);
                  const taken = takenByOther(row);
                  // Taken AND unticked → cannot be picked. Taken AND already
                  // ticked → leave it operable so drift can be cleared here.
                  const lockedOut = taken && !checked;
                  // A linked category with a blank English name would render
                  // “Already in “””; name the relationship instead.
                  const takenLabel =
                    row.linkedCategory && row.linkedCategory.name !== ''
                      ? t('category.products.takenBy', {
                          category: row.linkedCategory.name,
                        })
                      : t('category.products.takenByUnnamed');
                  return (
                    <tr
                      key={row.id}
                      data-testid={`category-products-row-${row.id}`}
                      data-taken={taken ? 'true' : undefined}
                      className={cn(
                        'border-b border-pw-border last:border-b-0',
                        checked && 'bg-pw-primary/5',
                        lockedOut && 'opacity-60',
                      )}
                    >
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={readOnly || lockedOut}
                          onChange={(e) => toggleRow(row.id, e.target.checked)}
                          aria-label={
                            lockedOut
                              ? takenLabel
                              : t('category.products.selectRowAria', { name: row.name })
                          }
                          title={lockedOut ? takenLabel : undefined}
                          data-testid={`category-products-check-${row.id}`}
                          className={cn(
                            'h-4 w-4 accent-pw-primary',
                            lockedOut ? 'cursor-not-allowed' : 'cursor-pointer',
                          )}
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded border border-pw-border bg-pw-surface">
                          {row.thumbnailUrl ? (
                            <img
                              src={row.thumbnailUrl}
                              alt=""
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-sm font-medium text-pw-text">
                        {row.name}
                        {taken ? (
                          <span
                            className="mt-0.5 block text-[12px] font-normal text-pw-text-muted"
                            data-testid={`category-products-taken-${row.id}`}
                          >
                            {takenLabel}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-pw-text-muted">
                        {row.blueprintCode || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-pw-text">
                        {t('category.products.skuCount', { count: row.variantCount })}
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge variant={row.status === 'Active' ? 'success' : 'secondary'}>
                          {t(`category.products.status.${row.status}`, {
                            defaultValue: row.status,
                          })}
                        </Badge>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {!showSelectedOnly && total > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-pw-text-muted">
              {t('category.products.showingRange', {
                from: (page - 1) * pageSize + 1,
                to: Math.min(page * pageSize, total),
                total,
              })}
            </span>
            <Pagination
              currentPage={page}
              pageCount={pageCount}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              testId="category-products-pagination"
              onChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-3 border-t border-pw-border pt-4">
          <span className="mr-auto text-sm text-pw-text-muted">
            {t('category.products.selectedCount', { count: selected.size })}
          </span>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="category-products-cancel"
          >
            {t('category.form.cancel')}
          </Button>
          <Button
            type="button"
            variant="default"
            disabled={readOnly}
            onClick={() => {
              onSave(Array.from(selected));
              onOpenChange(false);
            }}
            data-testid="category-products-save"
          >
            {t('category.form.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export default SelectProductsDialog;
